/**
 * The PWA update flow (docs/SYSTEMS.md section 5, "Updates").
 *
 * `vite.config.ts` runs the service worker with `registerType: 'prompt'`: a new worker
 * installs itself and then waits rather than taking over on its own. Two independent
 * triggers ask "is there something newer" -
 *
 * - `registration.update()`, which asks the browser to refetch `sw.js` and compare it
 *   byte for byte against the one it already has;
 * - `GET /api/health`, compared against `__APP_VERSION__`. This is the belt to the
 *   above's braces: an installed PWA's worker can be weeks stale, and asking the API
 *   directly catches a version the worker has not noticed yet, on top of also nudging
 *   `registration.update()` when the two disagree.
 *
 * Both fire on registration, on every return to a visible tab (`visibilitychange` to
 * visible and `focus`), on the `online` event, and every five minutes while the tab is visible (D280).
 * Installed iOS PWAs never fire `beforeinstallprompt` and can sit on a stale worker for a
 * long time between opens - the visible/focus triggers are what catch them up on resume.
 *
 * `onNeedRefresh` (from `virtual:pwa-register`) means a new worker has finished installing
 * and is waiting to take over. That only happens through `updateSW(true)`, which reloads
 * the page, and this class only calls it when it is safe: no envelope in flight or queued
 * (the store's `pending` count is zero) and either the player asked for it directly or the
 * tab has just come back from being hidden. An update is never applied out from under a
 * tap that has not been saved yet, and never on resume while the onboarding cards are
 * open: iOS fires a return from hidden on every app switch, and the reload would take the
 * player away from the card they were on (D46). The update waits for the world to exist.
 *
 * `onNeedRefresh` is never trusted to be the only word on it (D47). It can fire before this
 * class exists, so `game/sw-signal.ts` buffers it and replays it here; and every decision
 * that turns on "is something waiting" asks the registration itself through `hasWaiting`
 * rather than believing the flag alone. A worker found that way is treated exactly as if
 * the plugin had announced it: the flag is set and the pill appears.
 */

export type CheckResult = 'applied' | 'waiting' | 'coming' | 'up-to-date';
export type ApplyResult = 'applied' | 'not-ready' | 'not-safe';

export interface HealthReply {
  version: string;
}

export interface UpdaterDeps {
  /** `registration.update()`. Undefined in a fake or unsupported environment. */
  updateRegistration(): void;
  /**
   * Does the registration have a worker sitting in `waiting` right now (D47)? Asked rather
   * than inferred, because the plugin's `onNeedRefresh` can fire before there is anything
   * listening for it. Resolves false in a fake or unsupported environment.
   */
  hasWaiting(): Promise<boolean>;
  /** `GET /api/health`, no-store. Null when the request never reached a server. */
  fetchHealth(): Promise<HealthReply | null>;
  /** `updateSW` from `virtual:pwa-register`. `true` reloads the page once the new worker takes over. */
  updateSW(reloadPage?: boolean): Promise<void>;
  /** The store's count of envelopes in flight or queued. Zero is the only safe moment. */
  getPending(): number;
  /** True while the onboarding cards are open. A reload there loses the draft's place (D46). */
  isOnboarding(): boolean;
  /** `__APP_VERSION__`, the version this open of the app was built with. */
  appVersion: string;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  /** The store learns a new worker is installed and waiting, and can show the pill. */
  onUpdateReady(): void;
  /** The version `/api/health` last reported, so Settings can show it beside this build's. */
  onServerVersion(version: string): void;
}

/** How often a visible tab asks again, on top of the event-driven triggers (D280: minutes, not an hour). */
export const CHECK_INTERVAL_MS = 5 * 60_000;

/** "Check for updates" waits this long between asks while a worker installs (D47). */
export const WAITING_POLL_MS = 500;
/** And asks this many times, so about three seconds in all before it says up to date. */
export const WAITING_POLL_TRIES = 6;

/** No envelope outstanding: applying now cannot cut off a tap mid-flight. */
export function canApplyUpdate(pending: number): boolean {
  return pending <= 0;
}

export class Updater {
  private readonly d: UpdaterDeps;
  private ready = false;
  private timer: unknown = null;

  constructor(deps: UpdaterDeps) {
    this.d = deps;
  }

  /** True once `onNeedRefresh` has fired: a new worker is installed and waiting. */
  isReady(): boolean {
    return this.ready;
  }

  /** Called once, when the service worker has registered. Runs the first check and arms the repeating one. */
  start(): void {
    this.check();
    this.timer = this.d.setTimer(() => this.check(), CHECK_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) this.d.clearTimer(this.timer);
    this.timer = null;
  }

  /** `onNeedRefresh` from the plugin, replayed through the latch if it fired early (D47). */
  needRefresh(): void {
    this.markReady();
  }

  /** First word that a worker is waiting, from whichever source found it. Tells the store once. */
  private markReady(): void {
    if (this.ready) return;
    this.ready = true;
    this.d.onUpdateReady();
  }

  /**
   * True when a worker is waiting, believing the flag if it is already set and otherwise
   * asking the registration. Discovering one this way raises the pill, same as the plugin.
   */
  private async waitingNow(): Promise<boolean> {
    if (this.ready) return true;
    const waiting = await this.d.hasWaiting().catch(() => false);
    if (waiting) this.markReady();
    return waiting;
  }

  /** A one-shot delay on the injected timer, so tests drive it and no real clock is read. */
  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => {
      let handle: unknown = null;
      handle = this.d.setTimer(() => {
        this.d.clearTimer(handle);
        resolve();
      }, ms);
    });
  }

  /** Gives a worker that is still installing a few moments to reach `waiting` (D47). */
  private async waitForWaiting(): Promise<boolean> {
    for (let i = 0; i < WAITING_POLL_TRIES; i += 1) {
      if (await this.waitingNow()) return true;
      await this.pause(WAITING_POLL_MS);
    }
    return this.waitingNow();
  }

  /** Both triggers at once: ask the browser, and ask the API. Fire-and-forget. */
  check(): void {
    this.d.updateRegistration();
    void this.pollHealth().catch(() => false);
  }

  /**
   * A visible tab returning from hidden, a `focus`, or an `online` event. Runs the check
   * and, only when the tab just became visible again (not merely gained focus while
   * already visible) and it is safe to, applies a worker that is already waiting.
   */
  onResume(becameVisibleFromHidden: boolean): void {
    this.check();
    if (becameVisibleFromHidden) void this.applyIfSafe();
  }

  /** The player tapped the "Update ready" pill. */
  async requestApply(): Promise<ApplyResult> {
    if (!(await this.waitingNow())) return 'not-ready';
    if (!canApplyUpdate(this.d.getPending())) return 'not-safe';
    await this.d.updateSW(true);
    return 'applied';
  }

  /**
   * Settings' "Check for updates". Always resolves; a reload cuts it short when one happens.
   * When a worker is waiting but cannot be applied - onboarding is open, or a tap has not
   * saved - it says so rather than reporting up to date (D47). When the API reports a newer
   * version and no worker has reached `waiting` yet, it says the update is on the way.
   */
  async checkNow(): Promise<CheckResult> {
    if (await this.applyIfSafe()) return 'applied';
    if (this.ready) return 'waiting';
    this.d.updateRegistration();
    const stale = await this.pollHealth().catch(() => false);
    if (await this.waitForWaiting()) {
      if (await this.applyIfSafe()) return 'applied';
      return 'waiting';
    }
    return stale ? 'coming' : 'up-to-date';
  }

  /** Compares the API's version against this build's. A mismatch also nudges the worker. */
  private async pollHealth(): Promise<boolean> {
    const health = await this.d.fetchHealth();
    if (health !== null) this.d.onServerVersion(health.version);
    const stale = health !== null && health.version !== this.d.appVersion;
    if (stale) this.d.updateRegistration();
    return stale;
  }

  /** Applies a waiting worker when one exists and nothing is mid-flight. Reports whether it did. */
  private async applyIfSafe(): Promise<boolean> {
    if (!(await this.waitingNow())) return false;
    if (!canApplyUpdate(this.d.getPending())) return false;
    if (this.d.isOnboarding()) return false;
    await this.d.updateSW(true);
    return true;
  }
}
